import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { Field, FormikProvider, useFormik } from "formik";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import moment from "moment";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomSelect from "../../../CustomControls/CustomSelect";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";
import CustomSizePerPage from "../../../CustomControls/CustomSizePerPage";
import CustomPaginationButtons from "../../../CustomControls/CustomPaginationButtons";
import { post } from "../../../helpers/api_helper";
import {
  errorMessage,
  formatDateTime,
  successMessage,
  warningMessage,
} from "../CommonFunctions";
import {
  POST_MachineIntegrationGetMachineStatusList,
  POST_MachineIntegrationGetStagingList,
  POST_MachineIntegrationRejectStaging,
} from "../../../helpers/url_helper";
import { useFilter } from "../../../../context/FilterContext";

// Configuration > Machine > Machine Readings.
// Every reading an instrument computer has sent to the LIMS, newest first. A New reading has not been used yet; a reading is
// "Taken for a sample" when an analyst took it with the scale icon in Testing; a wrong New reading can be Rejected (it is kept
// with the reason, never deleted). Layout, filter bar, table and paging follow the other list screens (see Machines.js).

const PAGE_NAME = "machine-readings";

const STATUS_NEW = 1;
const STATUS_TAKEN = 2;
const STATUS_REJECTED = 3;

const statusOptions = [
  { label: "All", value: 0 },
  { label: "New (not used yet)", value: STATUS_NEW },
  { label: "Taken for a sample", value: STATUS_TAKEN },
  { label: "Rejected", value: STATUS_REJECTED },
];

const STATUS_VIEW = {
  [STATUS_NEW]: { cls: "bg-info text-dark", text: "New", hint: "Not used yet" },
  [STATUS_TAKEN]: { cls: "bg-success", text: "Taken for a sample", hint: "An analyst took this reading in Testing" },
  [STATUS_REJECTED]: { cls: "bg-danger", text: "Rejected", hint: "Marked as wrong; kept with the reason" },
};

const sevenDaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d;
};

const apiDay = (d) => (d ? moment(d).format("YYYY-MM-DD") : "");

const MachineReadings = () => {
  const { filter, updateFilter } = useFilter();
  const remembered = filter && filter?.page == PAGE_NAME;

  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const formsList = AuthUser?.Permissions?.FormList;
  const canReject =
    AuthUser?.UserType === 1 &&
    formsList?.filter((x) => x?.FormID == 25 && x?.IsEdit)?.length > 0;

  const [showFilter, setShowFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [readings, setReadings] = useState([]);
  const [machineOptions, setMachineOptions] = useState([]);

  const [recordCount, setRecordCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(remembered ? filter?.PageSize : 10);
  const [sortBy, setSortBy] = useState(remembered ? filter?.sortBy : "");
  const [orderBy, setOrderBy] = useState(remembered ? filter?.orderBy : "asc");

  const [rejecting, setRejecting] = useState(null);
  const [remarks, setRemarks] = useState("");

  const formikFilter = useFormik({
    onSubmit: () => fetchReadings(),
    initialValues: {
      MachineIDEncrypted: remembered ? filter?.MachineIDEncrypted : "",
      Status: remembered ? filter?.Status : 0,
      Search: remembered ? filter?.Search : "",
      FromDate: remembered ? filter?.FromDate : sevenDaysAgo(),
      ToDate: remembered ? filter?.ToDate : new Date(),
    },
    validateOnBlur: false,
  });

  const {
    values: { MachineIDEncrypted, Status, Search, FromDate, ToDate },
  } = formikFilter;

  useEffect(() => {
    if (ToDate && FromDate && ToDate < FromDate) {
      formikFilter.setFieldValue("ToDate", FromDate);
    }
  }, [FromDate]);

  // machines that read automatically, for the Machine filter
  useEffect(() => {
    post(POST_MachineIntegrationGetMachineStatusList, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
    })
      .then((response) => {
        setMachineOptions(
          (response?.List || [])
            .filter((x) => x.ResultSource == 2)
            .map((x) => ({ label: x.MachineName, value: x.MachineIDEncrypted })),
        );
      })
      .catch((error) => console.log(error));
    fetchReadings();
  }, []);

  // One page of readings from the API. The filter comes from the form (or `overrides`); page, page size and sort default to the
  // ones on the screen and are passed explicitly by the callers that have just changed them.
  const fetchReadings = async (overrides, paging = {}) => {
    const f = { MachineIDEncrypted, Status, Search, FromDate, ToDate, ...overrides };
    const page = paging.page ?? 1;
    const size = paging.size ?? currentSizePerPage;
    const sortColumn = paging.sortBy ?? sortBy;
    const sortOrder = paging.orderBy ?? orderBy;
    setIsLoading(true);

    updateFilter({
      page: PAGE_NAME,
      PageSize: size,
      sortBy: sortColumn,
      orderBy: sortOrder,
      ...f,
    });

    try {
      const response = await post(POST_MachineIntegrationGetStagingList, {
        CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
        BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
        MachineIDEncrypted: f.MachineIDEncrypted ?? "",
        TCNo: (f.Search ?? "").trim(),
        Status: Number(f.Status ?? 0),
        FromDate: apiDay(f.FromDate),
        ToDate: apiDay(f.ToDate),
        CurrentPage: page,
        PageSize: size,
        Sorting: sortColumn ? sortColumn + " " + sortOrder : "",
      });
      if (page > 1 && !response?.List?.length) {
        // the last reading of the last page is gone: show the page before it
        await fetchReadings(overrides, { ...paging, page: page - 1 });
        return;
      }
      setReadings(response?.IsSuccess ? response.List : []);
      setRecordCount(response?.IsSuccess ? response.TotalRecordCount : 0);
      setCurrentPage(page);
      setCurrentSizePerPage(size);
    } catch (error) {
      console.log(error);
      errorMessage("Could not load the readings!");
    }
    setIsLoading(false);
  };

  const getResetData = () => {
    const cleared = { MachineIDEncrypted: "", Status: 0, Search: "", FromDate: sevenDaysAgo(), ToDate: new Date() };
    formikFilter.setValues(cleared);
    setSortBy("");
    setOrderBy("asc");
    fetchReadings(cleared, { sortBy: "", orderBy: "asc" });
  };

  // click a heading: ascending, then descending, then back to newest first
  const handleClickSorting = (columnName) => {
    let next = { sortBy: columnName, orderBy: "asc" };
    if (sortBy == columnName) {
      next = orderBy == "asc" ? { sortBy: columnName, orderBy: "desc" } : { sortBy: "", orderBy: "asc" };
    }
    setSortBy(next.sortBy);
    setOrderBy(next.orderBy);
    fetchReadings(undefined, next);
  };

  // same signature the shared paging controls call
  const getData = (page = 1, sizePerPage = currentSizePerPage) => {
    fetchReadings(undefined, { page, size: sizePerPage });
  };

  const SortArrow = ({ column }) => (
    <span className="ml-1">
      {sortBy == column ? (
        orderBy == "desc" ? (
          <i className="fa fa-arrow-down ms-2 fs-14" style={{ opacity: "0.7" }} />
        ) : (
          <i className="fa fa-arrow-up ms-2 fs-14" style={{ opacity: "0.7" }} />
        )
      ) : (
        <i className="fa fa-sort ms-2 fs-14" style={{ opacity: "0.3" }} />
      )}
    </span>
  );

  const openReject = (item) => {
    setRejecting(item);
    setRemarks("");
  };

  const confirmReject = async () => {
    if (!remarks.trim()) {
      warningMessage("Write why the reading is rejected.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await post(POST_MachineIntegrationRejectStaging, {
        CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
        BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
        StagingIDEncrypted: rejecting.StagingIDEncrypted,
        RejectRemarks: remarks.trim(),
        RejectedByEncrypted: AuthUser?.ReferenceIDEncrypt ? AuthUser?.ReferenceIDEncrypt : "0",
      });
      if (response?.IsSuccess) {
        successMessage("The reading is rejected.");
        setRejecting(null);
        await fetchReadings(undefined, { page: currentPage });
        return;
      }
      warningMessage(response?.Message || "Could not reject the reading!");
    } catch (error) {
      console.log(error);
      errorMessage("Could not reject the reading!");
    }
    setIsLoading(false);
  };

  const detailsText = (item) => {
    if (item.Status === STATUS_TAKEN) return `Taken ${formatDateTime(item.AcceptedOn)} for sample ${item.TCNo}`;
    if (item.Status === STATUS_REJECTED) return `Rejected ${formatDateTime(item.RejectedOn)}: ${item.RejectRemarks}`;
    return "";
  };

  return (
    <div className="card m-1">
      <div className="card-body p-1">
        <Spin size="large" spinning={isLoading} tip={"Loading..."} style={{ position: "fixed" }}>
          <div className="table-responsive active-projects style-1 shorting">
            <div className="tbl-caption p-2">
              <h4 className="heading mb-0">Machine Readings</h4>
              <div className="d-flex align-items-center">
                <button type="button" className="btn btn-outline-primary btn-sm me-2" onClick={() => getResetData()}>
                  <i className="fa fa-window-restore" aria-hidden="true"></i>
                  {"  "}
                  Reset
                </button>
                <button type="button" className="btn btn-outline-primary btn-sm me-2" onClick={() => fetchReadings()}>
                  <i className="fa-solid fa-refresh me-2"></i> Reload
                </button>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setShowFilter(!showFilter)}>
                  <i className="fa-solid fa-filter me-2"></i>Filter
                </button>
              </div>
            </div>
            <hr className="mt-0 mb-1"></hr>
            <div className="p-2 pt-0 pb-0">
              {showFilter ? (
                <div>
                  <FormikProvider value={formikFilter}>
                    <Form onSubmit={formikFilter.handleSubmit}>
                      <Row className="me-0">
                        <Col lg={3}>
                          <Field
                            label="Machine"
                            name="MachineIDEncrypted"
                            placeholder={"All machines"}
                            options={machineOptions}
                            component={CustomSelect}
                            menuPosition="fixed"
                          />
                        </Col>
                        <Col lg={2}>
                          <Field
                            label="Status"
                            name="Status"
                            placeholder={"Select Status"}
                            options={statusOptions}
                            component={CustomSelect}
                            isClearable={false}
                            menuPosition="fixed"
                          />
                        </Col>
                        <Col lg={2}>
                          <Field
                            label="Sample No."
                            name="Search"
                            placeholder={"Part of the number"}
                            component={CustomInput}
                          />
                        </Col>
                        <Col lg={3}>
                          <div className="d-flex">
                            <Field
                              label="From Date"
                              name="FromDate"
                              placeholder={"Select From Date"}
                              component={CustomFlatpickr}
                              options={{ altInput: true, altFormat: "d-M-Y", dateFormat: "d-m-Y" }}
                            />
                            <div>&nbsp;</div>
                            <Field
                              label="To Date"
                              name="ToDate"
                              placeholder={"Select To Date"}
                              component={CustomFlatpickr}
                              options={{ altInput: true, altFormat: "d-M-Y", dateFormat: "d-m-Y", minDate: FromDate }}
                            />
                          </div>
                        </Col>
                        <Col lg={2}>
                          <label className="mb-1">&nbsp;</label>
                          <div className="d-flex">
                            <div className="d-lg-block">
                              <button type="button" className="btn btn-primary btn-sm mb-1" onClick={() => fetchReadings()}>
                                <i className="fa fa-search" aria-hidden="true"></i>
                                {"  "} Search
                              </button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </FormikProvider>
                </div>
              ) : (
                ""
              )}
              <div id="MachineReadings-tbl_wrapper" className="dataTables_wrapper no-footer">
                <table id="MachineReadings-tbl" className="table dataTable no-footer mb-0">
                  <thead>
                    <tr>
                      <th width="6%"></th>
                      <th style={{ width: "17%", cursor: "pointer" }} onClick={() => handleClickSorting("ReceivedOn")}>
                        Received <SortArrow column="ReceivedOn" />
                      </th>
                      <th style={{ width: "17%", cursor: "pointer" }} onClick={() => handleClickSorting("MachineName")}>
                        Machine <SortArrow column="MachineName" />
                      </th>
                      <th style={{ width: "10%", cursor: "pointer" }} onClick={() => handleClickSorting("MachineParameter")}>
                        Reading <SortArrow column="MachineParameter" />
                      </th>
                      <th style={{ width: "10%", cursor: "pointer" }} onClick={() => handleClickSorting("ResultValue")}>
                        Value <SortArrow column="ResultValue" />
                      </th>
                      <th style={{ width: "15%", cursor: "pointer" }} onClick={() => handleClickSorting("Status")}>
                        Status <SortArrow column="Status" />
                      </th>
                      <th style={{ textAlign: "left" }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readings.length > 0 ? (
                      readings.map((item) => {
                        const view = STATUS_VIEW[item.Status] || { cls: "bg-secondary", text: item.StatusName, hint: "" };
                        return (
                          <tr key={item.StagingIDEncrypted}>
                            <td>
                              {canReject && item.Status === STATUS_NEW ? (
                                <Link
                                  to="#"
                                  className="icon-box icon-box-md me-1"
                                  title="Reject"
                                  onClick={() => openReject(item)}
                                >
                                  <i className="fa fa-ban fs-15 text-danger"></i>
                                </Link>
                              ) : (
                                ""
                              )}
                            </td>
                            <td>{formatDateTime(item.ReceivedOn)}</td>
                            <td>{item.MachineName}</td>
                            <td>{item.MachineParameter}</td>
                            <td>
                              {item.ResultValue} {item.Unit}
                            </td>
                            <td>
                              <span className={`badge ${view.cls}`} title={view.hint}>
                                {view.text}
                              </span>
                            </td>
                            <td style={{ textAlign: "left" }}>{detailsText(item)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} style={{ textAlign: "left" }}>
                          <div>{"No Record Found..."}</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="d-sm-flex text-center justify-content-between align-items-center">
                  <CustomSizePerPage getData={getData} currentSizePerPage={currentSizePerPage} />
                  <div className="dataTables_info">
                    Showing {recordCount > 0 ? (currentPage - 1) * currentSizePerPage + 1 : 0} to{" "}
                    {recordCount > currentPage * currentSizePerPage ? currentPage * currentSizePerPage : recordCount} of{" "}
                    {recordCount} entries
                  </div>
                  <CustomPaginationButtons
                    totalPages={recordCount / currentSizePerPage}
                    currentPage={currentPage}
                    currentSizePerPage={currentSizePerPage}
                    getData={getData}
                  />
                </div>
              </div>
            </div>
          </div>
        </Spin>
      </div>

      <Modal className="fade" show={rejecting !== null} onHide={() => setRejecting(null)}>
        <Modal.Header>
          <Modal.Title>Reject Reading</Modal.Title>
          <Button type="button" variant="" className="btn-close" onClick={() => setRejecting(null)}></Button>
        </Modal.Header>
        <Modal.Body>
          {rejecting ? (
            <>
              <p className="mb-2">
                <strong>{rejecting.MachineName}</strong>: {rejecting.MachineParameter}{" "}
                <strong>
                  {rejecting.ResultValue} {rejecting.Unit}
                </strong>
                <br />
                <small className="text-muted">Received {formatDateTime(rejecting.ReceivedOn)}</small>
              </p>
              <label className="mb-1">
                Why is this reading wrong? <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                maxLength={500}
                autoFocus
                placeholder="For example: probe was still in the previous sample"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
              <small className="text-muted">
                The reading is not deleted. It stays in the list as Rejected, with this reason, and cannot be taken for a sample.
              </small>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={confirmReject}>
            Reject
          </Button>
          <Button variant="danger light" onClick={() => setRejecting(null)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MachineReadings;
